/**
 * Pure helpers for the QC "current issue" cursor. The cursor is stored as a
 * content-derived KEY (not a raw array index) so it survives the issues array
 * mutating on fix/edit/undo/preset-change and survives route switches. The
 * resolved index is always derived from the current array.
 *
 * Key = `${ruleId}:${cueIndex}:${occurrence}` where occurrence is the ordinal
 * among earlier issues sharing the same (ruleId, cueIndex). Occurrence keeps
 * duplicate (ruleId, cueIndex) pairs and multiple document-level (cueIndex -1)
 * issues distinct within a single list. It is POSITIONAL, though, so removing an
 * earlier sibling renumbers the survivors; {@link reconcileCursor} guards that
 * by re-finding the cursor's issue by content (incl. message) before falling
 * back to the positional successor.
 */

import type { QCIssue } from '../../core';

export type CursorKey = string;

/** Stable keys for every issue, in array order. */
export function issueKeys(issues: QCIssue[]): CursorKey[] {
  const seen = new Map<string, number>();
  return issues.map((i) => {
    const base = `${i.ruleId}:${i.cueIndex}`;
    const occ = seen.get(base) ?? 0;
    seen.set(base, occ + 1);
    return `${base}:${occ}`;
  });
}

/** Key of the issue at array index `i` (caller guarantees `i` is in range). */
export function keyAt(issues: QCIssue[], i: number): CursorKey {
  return issueKeys(issues)[i]!;
}

/** Array index of `key`, or -1 if no issue currently has it. */
export function indexOfKey(issues: QCIssue[], key: CursorKey | null): number {
  if (key == null) return -1;
  return issueKeys(issues).indexOf(key);
}

/**
 * Move the cursor one step. Clamps at both ends (no wrap). From a null or stale
 * key, a forward step lands on the first issue and a backward step on the last.
 * Returns null only for an empty list.
 */
export function stepKey(issues: QCIssue[], key: CursorKey | null, dir: 1 | -1): CursorKey | null {
  if (issues.length === 0) return null;
  const i = indexOfKey(issues, key);
  if (i < 0) return keyAt(issues, dir > 0 ? 0 : issues.length - 1);
  const next = Math.max(0, Math.min(i + dir, issues.length - 1));
  return keyAt(issues, next);
}

/**
 * Reconcile the cursor when `issues` is replaced by `next`. The single rule
 * behind fix-and-advance:
 *  - empty next, or no cursor → null
 *  - the cursor's issue still resolves in next → keep it (follows reorders/undo)
 *  - else, re-find the SAME issue by content (ruleId+cueIndex+message), which
 *    survives occurrence renumbering when an earlier same-(ruleId,cueIndex)
 *    sibling is removed → keep the cursor on it
 *  - else it was genuinely removed → land on whatever now occupies its old slot
 *    (the successor), clamped to the new length; null if absent from `prev`.
 */
export function reconcileCursor(
  prev: QCIssue[],
  key: CursorKey | null,
  next: QCIssue[],
): CursorKey | null {
  if (next.length === 0 || key == null) return null;
  if (indexOfKey(next, key) >= 0) return key;
  const oldIndex = indexOfKey(prev, key);
  if (oldIndex < 0) return null;
  const old = prev[oldIndex]!;
  const sameIssue = next.findIndex(
    (n) => n.ruleId === old.ruleId && n.cueIndex === old.cueIndex && n.message === old.message,
  );
  if (sameIssue >= 0) return keyAt(next, sameIssue);
  return keyAt(next, Math.min(oldIndex, next.length - 1));
}

/**
 * The cue index a cursor reveals, or null when the key is unresolved or the
 * issue is document-level (cueIndex < 0) — i.e. there is no cue to jump to.
 */
export function resolvedCueIndex(issues: QCIssue[], key: CursorKey | null): number | null {
  const i = indexOfKey(issues, key);
  if (i < 0) return null;
  const c = issues[i]!.cueIndex;
  return c >= 0 ? c : null;
}
